import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { format, toZonedTime } from 'date-fns-tz';
import { Modal } from 'antd';
import {
    removeBookmark,
    setBookmarks,
} from '../../features/bookMark/bookMarkSlice';
import CloseIcon from '../../assets/images/closeIcon.svg';
import DeleteIcon from '../../assets/images/DeleteIcon.svg';
import RightArrowIcon from '../../assets/images/righArrowIcon.svg';
import { goTOPageHref, handlePageChange } from '../../utils/readiumUtils';
import {
    useDeleteBookmarkMutation,
    useGetBookmarksQuery,
} from '../../api/bookmark';
import DotIcon from '../../assets/images/DotIcon.svg';

interface BookmarkLibraryModalProps {
    onClose: () => void;
    bookId: string;
    isBookmarkLibraryModalOpen: boolean;
    offset: number;
}

const BookmarkLibraryModal: React.FC<BookmarkLibraryModalProps> = ({
    onClose,
    bookId,
    isBookmarkLibraryModalOpen,
    offset,
}) => {
    const dispatch = useDispatch();
    const {
        data: bookmarks = [],
        isLoading,
        isError,
        refetch,
    } = useGetBookmarksQuery(bookId);
    dispatch(setBookmarks(bookmarks?.data));
    const [deleteBookmark, { isSuccess: isDeleteSuccess }] =
        useDeleteBookmarkMutation();

    const handleDeleteBookmark = async (id: number, href: string) => {
        try {
            dispatch(removeBookmark(id));
            await deleteBookmark({
                id: id,
                bookId: bookId,
            }).unwrap();
            window[0]?.updateBookmarkIcon(href, false);
        } catch (error) {
            console.error('Failed to delete bookmark:', error);
        }
    };

    // Handle navigation
    const handleRightArrowClick = (href: string) => {
        goTOPageHref(window, href);
        handlePageChange(window)
    };

    // Show confirmation modal before deleting
    const confirmDelete = (id: number, href: string) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this bookmark?',
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => handleDeleteBookmark(id, href),
        });
    };

    useEffect(() => {
        refetch();
    }, [isBookmarkLibraryModalOpen, isDeleteSuccess, refetch]);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-[60rem] h-[30rem] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center sticky top-0 bg-white z-10 border-gray-200 pb-2">
                    <h2 className="text-xl font-bold">Bookmarks Library</h2>
                    <button
                        data-testid="bookmark-library-close-button"
                        className="text-gray-500 hover:text-gray-700"
                        onClick={onClose}
                    >
                        <img src={CloseIcon} alt="Close" />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto flex-1 mt-4 custom-scrollbar">
                    {bookmarks?.data?.length > 0 ? (
                        <ul className="space-y-4">
                            {bookmarks?.data.map(
                                (bookmark: any, index: number) => (
                                    <li
                                        key={bookmark.id}
                                        onClick={() => {
                                            handleRightArrowClick(
                                                bookmark.href
                                            );
                                            handlePageChange(window);
                                            onClose();
                                        }}
                                        className="flex items-center justify-between p-4 rounded-xl border border-gray-300 shadow-sm hover:bg-[#F7F9FD] hover:cursor-pointer"
                                    >
                                        <div className="flex items-center space-x-4">
                                            <div className="flex items-center justify-center w-12 h-10 bg-blue-100 rounded-lg">
                                                <span className="text-black font-bold text-lg">
                                                    {index + 1}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                <div>
                                                    <h3 className="font-semibold text-black">
                                                        Page no:
                                                        {bookmark.page_no <= 0
                                                            ? 1
                                                            : bookmark.page_no}
                                                    </h3>
                                                </div>
                                                <div className="flex space-x-4">
                                                    {bookmark.chapter_name && (
                                                        <p className="text-sm text-gray-600 flex flex-row">
                                                            <div>
                                                                {
                                                                    bookmark.chapter_name
                                                                }
                                                            </div>
                                                        </p>
                                                    )}

                                                    <p className="text-sm text-gray-600 flex flex-row space-x-2">
                                                        <img
                                                            src={DotIcon}
                                                            alt=""
                                                        />
                                                        <div className="text-nowrap">
                                                            {format(
                                                                toZonedTime(
                                                                    new Date(
                                                                        bookmark.created_at
                                                                    ),
                                                                    'Asia/Kolkata'
                                                                ),
                                                                'dd MMM yyyy, hh:mm a',
                                                                {
                                                                    timeZone:
                                                                        'Asia/Kolkata',
                                                                }
                                                            )}
                                                        </div>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-6">
                                            <button
                                                data-testid="bookmark-library-item-delete-button"
                                                className="hover:bg-blue-100 rounded-full p-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    confirmDelete(
                                                        bookmark.id,
                                                        bookmark.href
                                                    );
                                                }}
                                            >
                                                <img
                                                    src={DeleteIcon}
                                                    alt="Delete"
                                                />
                                            </button>
                                            <button
                                                className="text-gray-500 hover:text-gray-700"
                                                data-testid="bookmark-library-item-goto-button"
                                            >
                                                <img
                                                    src={RightArrowIcon}
                                                    alt="Arrow"
                                                />
                                            </button>
                                        </div>
                                    </li>
                                )
                            )}
                        </ul>
                    ) : (
                        <p className="text-gray-500 text-center">
                            No bookmarks found.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookmarkLibraryModal;
